// Phase 3d — Tenant Feature Management admin console page.
// Per-tenant 3-state override grid (Default / On / Off) + monthly AI token
// cap. Uses /api/admin/tenants + /api/admin/tenants/{id}/feature-overrides.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/adminApi";
import { Check, X, RotateCcw, Save, Sparkles, Building2 } from "lucide-react";
import { useToast } from "../components/Toast";

// ---------------- helpers ---------------------------------------------------
const CAT_LABEL = {
  ai_manager: "AI Managers",
  business: "Business Features",
};
const PLAN_COLORS = {
  free: "text-[#5B617A]",
  standard: "text-blue-600",
  pro: "text-teal-600",
  premium: "text-pink-600",
};

function StatePill({ state }) {
  if (state === "on")
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">
        ON
      </span>
    );
  if (state === "off")
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
        OFF
      </span>
    );
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#7C5CFF]/[0.06] text-[#5B617A]">
      DEFAULT
    </span>
  );
}

// 3-state cycler button. state: undefined/null | "on" | "off"
function ThreeStateToggle({ state, onChange, testid }) {
  const base = "flex items-center gap-1 border rounded-xl px-3 py-1.5 text-xs font-semibold";
  return (
    <div className="inline-flex gap-1" data-testid={testid}>
      <button
        onClick={() => onChange(null)}
        data-testid={`${testid}-default`}
        className={`${base} ${state == null ? "bg-[#0B0F1A] text-white border-[#0B0F1A]" : "bg-white/60 text-[#5B617A] border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.04]"}`}
      >
        Default
      </button>
      <button
        onClick={() => onChange("on")}
        data-testid={`${testid}-on`}
        className={`${base} ${state === "on" ? "bg-green-600 text-white border-green-600" : "bg-white/60 text-green-700 border-green-200 hover:bg-green-50"}`}
      >
        <Check className="w-3 h-3" /> On
      </button>
      <button
        onClick={() => onChange("off")}
        data-testid={`${testid}-off`}
        className={`${base} ${state === "off" ? "bg-red-600 text-white border-red-600" : "bg-white/60 text-red-600 border-red-200 hover:bg-red-50"}`}
      >
        <X className="w-3 h-3" /> Off
      </button>
    </div>
  );
}

// ============================================================================
export default function FeatureOverridesPage() {
  const toast = useToast();
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [data, setData] = useState(null);   // /feature-overrides response
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null); // {type,msg}

  // Local editable state (mirrors data.overrides on load)
  const [featOverrides, setFeatOverrides] = useState({});
  const [tokenCap, setTokenCap] = useState("");

  // ---- Load tenant list once ----
  useEffect(() => {
    api.get("/tenants?page_size=200")
      .then((r) => {
        const items = r.data?.items || [];
        setTenants(items);
        if (!tenantId && items.length) setTenantId(items[0].tenant_id || items[0].id);
      })
      .catch(() => {});
  }, []);

  // ---- Load overrides whenever tenantId changes ----
  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await api.get(`/tenants/${tenantId}/feature-overrides`);
      setData(r.data);
      setFeatOverrides({ ...(r.data.overrides.features || {}) });
      setTokenCap(
        r.data.overrides.ai_monthly_token_cap != null
          ? String(r.data.overrides.ai_monthly_token_cap)
          : "",
      );
    } catch (e) {
      setFlash({ type: "error", msg: "Failed to load overrides." });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  // ---- Grouped feature registry ----
  const grouped = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const f of data.registry) {
      (g[f.category] ||= []).push(f);
    }
    return g;
  }, [data]);

  // ---- Live "Tenant will see" resolver ----
  const resolveLive = useCallback(
    (slug, minPlan) => {
      const state = featOverrides[slug];
      if (state === "off") return { visible: false, reason: "admin_off" };
      if (state === "on") return { visible: true, reason: "admin_on" };
      // Plan meets check
      const rank = { free: 0, standard: 1, pro: 2, premium: 3 };
      const eff = data?.effective_plan || "free";
      const ok = (rank[eff] ?? 0) >= (rank[minPlan] ?? 0);
      return { visible: ok, reason: ok ? "plan_meets" : "plan_gated" };
    },
    [featOverrides, data],
  );

  const dirty = useMemo(() => {
    if (!data) return false;
    const origFeat = data.overrides.features || {};
    const origCap = data.overrides.ai_monthly_token_cap ?? "";
    const curCap = tokenCap === "" ? "" : Number(tokenCap);
    const origKeys = Object.keys(origFeat);
    const curKeys = Object.keys(featOverrides);
    if (origKeys.length !== curKeys.length) return true;
    for (const k of curKeys) if (origFeat[k] !== featOverrides[k]) return true;
    return (origCap === "" ? "" : Number(origCap)) !== curCap;
  }, [featOverrides, tokenCap, data]);

  const totalOverrides = useMemo(
    () => Object.values(featOverrides).filter((v) => v === "on" || v === "off").length,
    [featOverrides],
  );

  const setOne = (slug, state) => {
    setFeatOverrides((prev) => {
      const next = { ...prev };
      if (state == null) delete next[slug];
      else next[slug] = state;
      return next;
    });
  };

  const resetAll = () => {
    if (!window.confirm("Clear ALL feature overrides and the AI token cap for this tenant?")) return;
    setFeatOverrides({});
    setTokenCap("");
  };

  const save = async () => {
    setSaving(true); setFlash(null);
    try {
      const payload = {
        features: featOverrides,
        ai_monthly_token_cap: tokenCap === "" ? null : Math.max(0, Number(tokenCap) || 0),
      };
      await api.patch(`/tenants/${tenantId}/feature-overrides`, payload);
      setFlash({ type: "success", msg: `Saved. ${totalOverrides} override(s) active.` });
      toast.success(`Saved · ${totalOverrides} override(s) active`);
      await load();
    } catch (e) {
      const reason = e.response?.data?.detail || "Save failed";
      setFlash({ type: "error", msg: reason });
      toast.error(`Failed to save — ${typeof reason === "string" ? reason : "server error"}`);
    } finally {
      setSaving(false);
      setTimeout(() => setFlash(null), 3500);
    }
  };

  const tenant = data;

  return (
    <div className="p-8 glass-animate" data-testid="feature-overrides-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0B0F1A]">Feature Overrides</h1>
          <p className="text-sm text-[#5B617A] mt-0.5">
            Force plan-gated features on or off for a specific tenant · bypass or restrict independently of billing.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 py-1.5">
            <Building2 className="w-4 h-4 text-[#5B617A]" />
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              data-testid="tenant-picker"
              className="bg-transparent text-sm font-semibold text-[#0B0F1A] focus:outline-none min-w-[240px]"
            >
              {tenants.map((t) => (
                <option key={t.tenant_id || t.id} value={t.tenant_id || t.id}>
                  {t.name} · {t.plan}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Flash */}
      {flash ? (
        <div
          data-testid="flash-msg"
          className={`mb-4 px-4 py-2 rounded-xl text-sm font-semibold ${
            flash.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          {flash.msg}
        </div>
      ) : null}

      {loading || !tenant ? (
        <div className="text-[#5B617A] text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Left: grid */}
          <div className="col-span-8">
            {/* Summary strip */}
            <div className="bg-white/60 rounded-[20px] border border-[#7C5CFF]/[0.12] p-4 mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-[#5B617A] font-semibold">
                  {tenant.tenant_name}
                </div>
                <div className="text-xs text-[#5B617A]">
                  effective plan:{" "}
                  <span className={`font-bold ${PLAN_COLORS[tenant.effective_plan]}`}>
                    {tenant.effective_plan}
                  </span>{" "}
                  · stored plan: {tenant.plan}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#5B617A]">
                  <b className="text-[#0B0F1A]">{totalOverrides}</b> override(s) active
                </span>
                <button
                  onClick={resetAll}
                  data-testid="reset-all-btn"
                  className="px-3 py-1.5 rounded-xl bg-[#7C5CFF]/[0.06] text-[#5B617A] text-xs font-semibold hover:bg-[#7C5CFF]/[0.06] flex items-center gap-1"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Reset all
                </button>
                <button
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="save-btn"
                  className={`px-4 py-1.5 rounded-xl text-white text-sm font-semibold flex items-center gap-1 ${
                    !dirty || saving
                      ? "bg-slate-300 cursor-not-allowed"
                      : "bg-[#7C5CFF] hover:bg-[#5A3FD9]"
                  }`}
                >
                  <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            {/* AI Token Cap */}
            <div className="bg-white/60 rounded-[20px] border border-[#7C5CFF]/[0.12] p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-pink-500" />
                <h3 className="text-sm font-bold text-[#0B0F1A]">
                  Monthly AI Token Cap
                </h3>
              </div>
              <p className="text-xs text-[#5B617A] mb-2">
                Override the plan default monthly token quota. Leave blank to use plan
                default. Stored now; enforcement middleware ships in the next slice.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step={10000}
                  placeholder="e.g. 500000"
                  value={tokenCap}
                  onChange={(e) => setTokenCap(e.target.value)}
                  data-testid="token-cap-input"
                  className="border border-[#7C5CFF]/[0.15] rounded-xl px-3 py-1.5 text-sm w-48"
                />
                <span className="text-xs text-[#5B617A]">tokens/month</span>
              </div>
            </div>

            {/* Grouped grids */}
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} className="bg-white/60 rounded-[20px] border border-[#7C5CFF]/[0.12] mb-4 overflow-hidden">
                <div className="px-4 py-3 border-b border-[#7C5CFF]/[0.04] flex items-center justify-between">
                  <h2 className="text-sm font-bold text-[#0B0F1A]">{CAT_LABEL[cat] || cat}</h2>
                  <span className="text-[10px] text-[#5B617A] font-semibold uppercase tracking-wider">
                    {items.length} features
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {items.map((f) => {
                    const state = featOverrides[f.slug] ?? null;
                    return (
                      <div
                        key={f.slug}
                        className="flex items-center px-4 py-3"
                        data-testid={`row-${f.slug}`}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-[#0B0F1A]">
                            {f.label}
                          </div>
                          <div className="text-[11px] text-[#5B617A] mt-0.5">
                            slug: <code className="bg-[#7C5CFF]/[0.06] px-1 rounded">{f.slug}</code>{" "}
                            · requires{" "}
                            <span className={`font-bold ${PLAN_COLORS[f.min_plan]}`}>
                              {f.min_plan}
                            </span>
                          </div>
                        </div>
                        <div className="mr-4">
                          <StatePill state={state} />
                        </div>
                        <ThreeStateToggle
                          state={state}
                          onChange={(v) => setOne(f.slug, v)}
                          testid={`toggle-${f.slug}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Right: live preview */}
          <aside className="col-span-4" data-testid="preview-panel">
            <div className="bg-white/60 rounded-[20px] border border-[#7C5CFF]/[0.12] sticky top-4 overflow-hidden">
              <div className="px-4 py-3 border-b border-[#7C5CFF]/[0.04] bg-[#0B0F1A] text-white">
                <div className="text-[10px] uppercase tracking-wider text-[#5B617A] font-semibold">
                  Live Preview
                </div>
                <h3 className="text-sm font-bold">Tenant will see</h3>
              </div>
              <div className="p-3 max-h-[70vh] overflow-y-auto text-xs">
                {Object.entries(grouped).map(([cat, items]) => (
                  <div key={cat} className="mb-3">
                    <div className="text-[10px] uppercase font-bold text-[#5B617A] tracking-wider mb-1.5">
                      {CAT_LABEL[cat]}
                    </div>
                    {items.map((f) => {
                      const live = resolveLive(f.slug, f.min_plan);
                      return (
                        <div
                          key={f.slug}
                          data-testid={`preview-${f.slug}`}
                          className="flex items-center justify-between py-1"
                        >
                          <span className={live.visible ? "text-[#0B0F1A]" : "text-[#5B617A] line-through"}>
                            {f.label}
                          </span>
                          <span
                            className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                              live.reason === "admin_on"
                                ? "bg-green-600 text-white"
                                : live.reason === "admin_off"
                                ? "bg-red-600 text-white"
                                : live.reason === "plan_meets"
                                ? "bg-slate-200 text-[#5B617A]"
                                : "bg-[#7C5CFF]/[0.06] text-[#5B617A]"
                            }`}
                          >
                            {live.reason}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
                <div className="border-t border-[#7C5CFF]/[0.04] pt-2 mt-2 flex items-center justify-between text-[#5B617A]">
                  <span>Token cap</span>
                  <span className="font-bold text-[#0B0F1A]">
                    {tokenCap === "" ? "plan default" : Number(tokenCap).toLocaleString() + " / mo"}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
